import { Fragment, useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  RiCloudLine,
  RiDownloadLine,
  RiFileCopyLine,
} from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  ExternalLink,
  Flex,
  InfraField,
  InfraSection,
  InfraStack,
  Text,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';

export const GcpInfrastructureInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const instanceId = annotations['gcp.infrastructure/instance-id'];
  const instanceName = annotations['gcp.infrastructure/instance-name'];
  const machineType = annotations['gcp.infrastructure/machine-type'];
  const publicIp = annotations['gcp.infrastructure/public-ip'];
  const zone = annotations['gcp.infrastructure/zone'];
  const network = annotations['gcp.infrastructure/network'];
  const consoleUrl = annotations['gcp.infrastructure/console-url'];
  const sshCommandAnnotation = annotations['gcp.infrastructure/ssh-command'];
  const sshPrivateKeyEncoded =
    annotations['gcp.infrastructure/ssh-private-key'];
  const environmentName =
    annotations['infrastructure.backstage.io/environment-name'];
  const templateType = annotations['infrastructure.backstage.io/type'];
  const envId = annotations['infrastructure.backstage.io/env-id'];
  const domain = annotations['gcp.infrastructure/domain'];
  const applicationPort = annotations['gcp.infrastructure/application-port'];
  const applicationUrlAnnotation =
    annotations['gcp.infrastructure/application-url'];
  const applicationUrl =
    applicationUrlAnnotation ||
    (applicationPort && envId && domain
      ? `https://${applicationPort}-p-${envId}.${domain}`
      : undefined);

  const splitList = (key: string): string[] => {
    const raw = annotations[key];
    return raw ? raw.split('|||').filter(Boolean) : [];
  };
  const instanceNames = splitList('gcp.infrastructure/instance-names');
  const instanceIds = splitList('gcp.infrastructure/instance-ids');
  const publicIps = splitList('gcp.infrastructure/public-ips');
  const privateIps = splitList('gcp.infrastructure/private-ips');
  const sshCommands = splitList('gcp.infrastructure/ssh-commands');
  const consoleUrls = splitList('gcp.infrastructure/console-urls');
  const gcloudSshCommands = splitList('gcp.infrastructure/gcloud-ssh-commands');
  const isMultiVm = instanceNames.length > 1;
  const isEmptyVm = templateType === 'gcp-empty-vm';

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return atob(sshPrivateKeyEncoded);
    } catch {
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const withKeyFlag = (cmd: string | undefined, ip: string | undefined) => {
    if (cmd?.includes('-i ')) return cmd;
    if (!ip) return cmd || '';
    return `ssh -i <PATH_TO_YOUR_PRIVATE_KEY> ubuntu@${ip}`;
  };
  const sshCommand = withKeyFlag(sshCommandAnnotation, publicIp);
  const gcloudSshCommand =
    annotations['gcp.infrastructure/gcloud-ssh-command'];
  const downloadedKeyCommand = publicIp
    ? `ssh -i empty_vm_key.pem ubuntu@${publicIp}`
    : '';
  const hasGcpInfo =
    instanceId || publicIp || network || machineType || isMultiVm;
  const isGCP = templateType?.startsWith('gcp-');

  if (
    !hasGcpInfo ||
    !isGCP ||
    templateType === 'gcp-gke-cluster' ||
    templateType === 'gcp-redis-crdb' ||
    templateType === 'gcp-redis-oss-vm' ||
    templateType === 'gcp-redis-enterprise'
  ) {
    return null;
  }

  const title =
    templateType === 'gcp-empty-vm'
      ? 'Empty GCP VM'
      : templateType === 'gcp-image-launch'
        ? 'Portal Image Instance'
        : templateType === 'gcp-memtier-benchmark'
          ? 'Memtier Benchmark VM'
          : templateType === 'gcp-riotx'
            ? 'RIOT-X Migration VM'
            : 'GCP Infrastructure';

  const downloadSSHKey = () => {
    if (!sshPrivateKey) return;
    const blob = new Blob([sshPrivateKey], { type: 'application/x-pem-file' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'empty_vm_key.pem';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <InfoCard
      title={title}
      subheader={`Environment: ${environmentName || 'N/A'}`}
      icon={<RiCloudLine size={20} />}
    >
      <InfraStack>
        {applicationUrl && (
          <InfraSection title="Application">
            <InfraField
              label="Application URL:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={applicationUrl}>
                    {applicationUrl}
                  </ExternalLink>
                  <CopyButton text={applicationUrl} />
                </span>
              }
              mono={false}
            />
            {applicationPort && (
              <InfraField label="Application Port:" value={applicationPort} />
            )}
          </InfraSection>
        )}

        <InfraSection
          title={`🖥️ ${isMultiVm ? `Compute Instances (${instanceNames.length})` : 'Compute Instance'}`}
        >
          {machineType && (
            <InfraField label="Machine Type:" value={machineType} />
          )}
          {zone && <InfraField label="Zone:" value={zone} />}

          {!isMultiVm && instanceId && (
            <InfraField
              label="Instance ID:"
              value={
                <span className={styles.copyRow}>
                  {consoleUrl ? (
                    <ExternalLink href={consoleUrl}>{instanceId}</ExternalLink>
                  ) : (
                    instanceId
                  )}
                  <CopyButton text={instanceId} />
                </span>
              }
              mono={false}
            />
          )}
          {!isMultiVm && instanceName && (
            <InfraField label="Instance Name:" value={instanceName} />
          )}
          {!isMultiVm && publicIp && (
            <CopyableValue label="Public IP:" value={publicIp} />
          )}

          {isMultiVm &&
            instanceNames.map((vmName, idx) => {
              const vmId = instanceIds[idx];
              const vmPubIp = publicIps[idx];
              const vmPrivIp = privateIps[idx];
              const vmSsh = withKeyFlag(sshCommands[idx], vmPubIp);
              const vmGcloudSsh = gcloudSshCommands[idx];
              const vmConsole = consoleUrls[idx];
              return (
                <Fragment key={vmName}>
                  <InfraField
                    label={`VM #${idx + 1}:`}
                    value={vmName}
                    mono={false}
                  />
                  {vmId && (
                    <InfraField
                      label="Instance ID:"
                      value={
                        vmConsole ? (
                          <ExternalLink href={vmConsole}>{vmId}</ExternalLink>
                        ) : (
                          vmId
                        )
                      }
                      mono={false}
                    />
                  )}
                  {vmPubIp && (
                    <CopyableValue label="Public IP:" value={vmPubIp} />
                  )}
                  {vmPrivIp && (
                    <InfraField label="Private IP:" value={vmPrivIp} />
                  )}
                  {vmSsh && <CopyableValue label="SSH Command:" value={vmSsh} />}
                  {vmGcloudSsh && (
                    <CopyableValue label="gcloud SSH:" value={vmGcloudSsh} />
                  )}
                </Fragment>
              );
            })}
        </InfraSection>

        {(sshCommand || sshPrivateKey || isEmptyVm) && (
          <InfraSection title="🔐 SSH Access">
            {sshPrivateKey ? (
              <>
                <Text variant="body-small">
                  A key pair was auto-generated for this VM. Download it, then
                  connect with <code>-i</code>:
                </Text>
                <Flex gap="2">
                  <Button variant="primary" size="small" onPress={downloadSSHKey}>
                    <RiDownloadLine size={16} />
                    Download Key
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                  >
                    <RiFileCopyLine size={16} />
                    Copy Key
                  </Button>
                </Flex>
                <Text variant="body-small">
                  Then run <code>chmod 400 empty_vm_key.pem</code>
                </Text>
                {downloadedKeyCommand && (
                  <CopyableValue
                    label="SSH Command:"
                    value={downloadedKeyCommand}
                  />
                )}
              </>
            ) : (
              <>
                {sshCommand && (
                  <CopyableValue label="SSH Command:" value={sshCommand} />
                )}
                {gcloudSshCommand && (
                  <CopyableValue label="gcloud SSH:" value={gcloudSshCommand} />
                )}
                <Text variant="body-small">
                  Replace <code>&lt;PATH_TO_YOUR_PRIVATE_KEY&gt;</code> with the
                  private key that matches the public key you pasted at
                  provision (e.g.{' '}
                  <code>ssh -i ~/.ssh/ps-empty-vm ubuntu@…</code>). A bare{' '}
                  <code>ssh user@ip</code> only works if that key is your SSH
                  default identity.
                </Text>
              </>
            )}
          </InfraSection>
        )}
      </InfraStack>
    </InfoCard>
  );
};
